<template>
  <Portal to="modalPortalTarget">
    <ModalStructure
      ref="modal"
      class="passwordUpdateModal"
      :shouldCloseOnBlur="false"
      :showModal="openModal"
      showCloseButton
      @close="handleClose"
    >
      <template v-slot:header>
        <HeadingSmall class="header">
          Set New Password
        </HeadingSmall>
      </template>
      <template v-slot:body>
        <HeadingMedium
          v-if="fullName"
          class="name"
        >
          {{ fullName }}
        </HeadingMedium>
        <NotificationInPage
          v-if="!hideErrors && hasErrors"
          class="errorNotification"
          :notification="errorNotificationConfig"
          @close="hideErrors = true"
        >
          <template v-slot:message>
            <HeadingExtraSmall
              class="notificationHeading"
            >
              <MarkdownBlock
                :mdTemplate="errorMarkdown"
              />
            </HeadingExtraSmall>
          </template>
        </NotificationInPage>
        <PasswordComplexity
          :passwordHistoryFailed="passwordHistoryFailed"
          :passwordPolicy="passwordPolicy"
          :username="user.username"
          :value="newPassword"
          @blur="resizeModal"
          @focus="resizeModal"
          @input="handlePasswordChange"
          @isValidPassword="handleIsValidPassword"
        />
      </template>
      <template v-slot:footer>
        <div class="footer">
          <ButtonGroupSave
            class="buttonGroup"
            :isDisabledPrimary="!isValidPassword"
            :isFullWidth="isSmallScreen()"
            :isLoadingPrimary="isSaving"
            :primaryButtonProps="$options.primaryButtonProps"
            @primaryAction="handleSave"
            @secondaryAction="handleCancel"
          />
        </div>
      </template>
    </ModalStructure>
  </Portal>
</template>

<script>
import {
  ButtonGroupSave,
  HeadingExtraSmall,
  HeadingMedium,
  HeadingSmall,
  MarkdownBlock,
  ModalStructure,
  NotificationInPage,
  Types,
} from '@jumpcloud/ui-components';
import { mapActions } from 'vuex';
import PasswordComplexity, { serverValidationMessages } from 'client/systemusers/components/PasswordComplexity.vue';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import Util from 'client/util/Util';

export default {
  name: 'PasswordUpdateModal',

  components: {
    ButtonGroupSave,
    HeadingExtraSmall,
    HeadingSmall,
    HeadingMedium,
    MarkdownBlock,
    ModalStructure,
    NotificationInPage,
    PasswordComplexity,
  },

  props: {
    fullName: {
      type: String,
      default: undefined,
    },

    openModal: Boolean,

    passwordPolicy: {
      type: Object,
      default: () => ({}),
    },

    user: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    hideErrors: false,
    isSavedSuccessfully: false,
    isSaving: false,
    isValidPassword: false,
    newPassword: '',
    serverValidationErrors: [],
  }),

  primaryButtonProps: Types.PrimaryButtonProps({
    text: 'Save Changes',
  }),

  computed: {
    errorMarkdown() {
      return this.formattedServerValidationErrors
        .map(err => `${err}  `)
        .join('\n');
    },

    errorNotificationConfig() {
      return Types.NotificationConfig({
        showCloseButton: true,
        type: 'error',
        icon: 'error',
      });
    },

    formattedServerValidationErrors() {
      const errors = [];

      if (this.passwordHistoryFailed) {
        errors.push(serverValidationMessages.passwordHistory(this.passwordPolicy.maxHistory));
      }

      return errors;
    },

    hasErrors() {
      return this.serverValidationErrors.length > 0;
    },

    passwordHistoryFailed() {
      return this.serverValidationErrors.includes('passwordHistory');
    },
  },

  watch: {
    openModal() {
      // Reset the component state when openModal changes, because this component
      // doesn't get destroyed when the modal is hidden
      Object.assign(this, this.$options.data());
    },

    serverValidationErrors: {
      deep: true,
      handler() {
        // if the errors array changes at all, show the errors again
        this.hideErrors = false;
      },
    },
  },

  methods: {
    ...mapActions('AlertModule', ['triggerSuccessAlert', 'triggerErrorAlert']),

    handleCancel() {
      this.$emit('cancel');
    },

    handleClose() {
      this.$emit('close', this.isSavedSuccessfully);
    },

    handleIsValidPassword(isValidPassword) {
      this.isValidPassword = isValidPassword;
    },

    handlePasswordChange(password) {
      this.newPassword = password;
    },

    async handleSave() {
      try {
        this.isSaving = true;

        const response = await SystemUserApi
          .changePassword(this.user._id, this.newPassword);

        if (response === true) {
          this.isSavedSuccessfully = true;
          this.triggerSuccessAlert('Password updated successfully');
          this.handleClose();
        } else {
          this.serverValidationErrors = response;
        }
      } catch (e) {
        this.triggerErrorAlert('Could not save password');
      } finally {
        this.isSaving = false;
      }
    },

    isSmallScreen: Util.isSmallScreen,

    async resizeModal() {
      // this method is only needed on mobile devices
      if (!this.isSmallScreen()) {
        return;
      }

      const KEYBOARD_ANIMATION_DURATION = 700;

      // visualViewport.height does not update until after the keyboard's
      // entry/exit animations have completed
      await Util.sleep(KEYBOARD_ANIMATION_DURATION);

      /**
       * @todo create a css variable binding with the value of visualViewport.height to use here
       * and anywhere else the height needs to match the visual viewport
       *
       * https://github.com/TheJumpCloud/jumpcloud-ui-components/pull/443#issuecomment-1230725433
       */
      this.$refs.modal.$el.firstChild.style.height = `${window.visualViewport.height}px`;
    },
  },
};
</script>

<style scoped>
.passwordUpdateModal {
  max-width: 100%;
  text-align: left;
  width: 33rem;
}

@media screen and (max-width: 768px) {
  .passwordUpdateModal.passwordUpdateModal {
    align-items: flex-start;
    padding: 0;
  }

  .passwordUpdateModal > :first-child {
    border-radius: 0;
    flex-grow: 1;
    margin: 0;
  }
}

.buttonGroup {
  flex: auto;
}

.name {
  font-size: 1.25rem;
}

.footer {
  flex: 1 0 auto;
  margin-top: calc(-1 * var(--jc-spacer));
}

.errorNotification {
  margin-bottom: var(--jc-spacer);
}

.header.header {
  border-bottom: 1px solid var(--jcGrey200);
  font-size: 1.125rem;
  margin-left: calc(-1 * var(--modal-padding));
  margin-right: calc(-1 * var(--modal-padding));
  padding: var(--modal-padding);
  padding-top: 0;
}

.notificationHeading.notificationHeading {
  font-weight: 600;
}
</style>
